import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getLatestInsights } from '@/data/insights';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const InsightsTeaser = () => {
  const insights = getLatestInsights(3);
  const imageSizes = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';

  return (
    <section className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">Ratgeber</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold mt-4 mb-6 text-foreground">
            Welches Wissen teilen wir
            <span className="gradient-text"> im Ratgeber?</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Praxisnahes Webdesign- und SEO-Wissen aus Wernigerode. Kurz, konkret und direkt anwendbar.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {insights.map((insight, index) => (
            <motion.div
              key={insight.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={`/ratgeber/${insight.slug}`}
                className="group block h-full"
              >
                <div className="relative rounded-2xl overflow-hidden bg-card border border-border shadow-lg hover:shadow-xl transition-all h-full">
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={buildSrc(insight.heroImage, 1024)}
                      srcSet={buildSrcSet(insight.heroImage, IMAGE_WIDTHS.card)}
                      sizes={imageSizes}
                      alt={insight.heroImageAlt}
                      loading="lazy"
                      decoding="async"
                      width={1024}
                      height={730}
                      style={insight.cardImagePosition ? { objectPosition: insight.cardImagePosition } : undefined}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-white/90 text-xs font-medium text-card-foreground shadow-sm">
                        {insight.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
                      <span>{formatDate(insight.datePublished)}</span>
                      <span>•</span>
                      <span>{insight.readingTime}</span>
                    </div>
                    <h3 className="text-xl font-display font-semibold mb-2 text-card-foreground group-hover:text-primary transition-colors">
                      {insight.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                      {insight.excerpt}
                    </p>
                    <span className="text-primary text-sm font-medium inline-flex items-center gap-1">
                      Artikel lesen
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/ratgeber"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
          >
            Alle Ratgeberartikel ansehen
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default InsightsTeaser;
